import { Children, Fragment, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import type { AlertColor, Block } from '@w/content-blocks'
import { Button } from '@w/ui/components/button'
import { CircleAlertIcon, CircleCheckIcon, InfoIcon, OctagonAlertIcon } from '@w/ui/components/icon'
import { IconButton } from '@w/ui/components/icon-button'
import { Popover, PopoverDescription, PopoverPopup, PopoverTitle, PopoverTrigger } from '@w/ui/components/popover'
import { blockEditorTheme } from '../../theme'
import { columnPercent, dragResizeColumns, keyResizeColumns } from '../../core/columns'
import { selectedCellIds } from '../dom-selection'
import { children as blockChildren, isListed } from '../../core/tree-read'
import type { BlockView, CoreViewApi } from '../view-api'
import { RichField, TextField } from './field'
import { useBlockViewServices } from './services'

const tones: AlertColor[] = ['info', 'success', 'warning', 'error']

/** Media players contain flow elements; their prose container must allow them. So does a paragraph holding an answer
 * part under host practice (`practiceMarks` given): the question's controls after the blank hold flow content (a recorder). */
export function renderParagraph(block: Extract<Block, { type: 'p' }>, api: CoreViewApi, children: ReactNode, key?: string,
  practiceMarks?: Readonly<Record<string, number | null>>) {
  const containsFlow = (item: Block): boolean => ['audio', 'video', 'img', 'file', 'preview', 'question', 'quote'].includes(item.type) ||
    item.type === 'cloze' && api.mode === 'answer' && (practiceMarks !== undefined || !!item.$showAnswer && !!item.answerAudio)
    || blockChildren(item).some(child => containsFlow(child))
  const Tag = block.children.some(child => containsFlow(child)) ? 'div' : 'p'
  const topLevel = isListed(api.state.blocks, block)
  return <Tag key={key} data-block-id={block.id} className={blockEditorTheme.paragraph} draggable={api.mode === 'edit' && topLevel || undefined}
    style={block.align ? { textAlign: block.align } : undefined}>
    {children}{api.mode !== 'review' && block.children.at(-1)?.type === 'br' && <br />}
  </Tag>
}
export const ParagraphView: BlockView = ({ block, api, children }) => block.type === 'p' ? renderParagraph(block, api, children) : null
const toneClasses = {
  info: 'border-surface-info bg-surface-info [&>[data-alert-field=title]]:text-on-surface-info [&>[data-alert-field=body]]:text-on-surface-info-secondary',
  success: 'border-surface-success bg-surface-success [&>[data-alert-field=title]]:text-on-surface-success [&>[data-alert-field=body]]:text-on-surface-success-secondary',
  warning: 'border-surface-warning bg-surface-warning [&>[data-alert-field=title]]:text-on-surface-warning [&>[data-alert-field=body]]:text-on-surface-warning-secondary',
  error: 'border-surface-error bg-surface-error [&>[data-alert-field=title]]:text-on-surface-error [&>[data-alert-field=body]]:text-on-surface-error-secondary',
}

export const AlertView: BlockView = ({ block, api, children }) => {
  const { updateBlock } = useBlockViewServices()
  const [open, setOpen] = useState(false)
  if (block.type !== 'alert') return null
  const icon = block.color === 'error' ? <CircleAlertIcon /> : block.color === 'success' ? <CircleCheckIcon />
    : block.color === 'warning' ? <OctagonAlertIcon /> : <InfoIcon />
  // Inside the card's padding, top-right: never past the column edge.
  const iconClass = 'bg-surface-1 absolute top-3 end-3 flex size-7 items-center justify-center rounded-full p-1.5 [&>svg]:size-full [&>svg]:overflow-visible'
  return <aside data-block-id={block.id} data-testid={`content-blocks-editor-alert-${block.id}`} role="alert"
    className={`group/alert text-on-surface text-body-medium relative my-5 inline-grid w-full items-start gap-x-2 gap-y-0.5 rounded-2xl border p-4 pe-12 text-sm ${toneClasses[block.color]}`}>
    {block.title !== undefined && <TextField api={api} as="div" data-alert-field="title" data-testid={`alert-title-${block.id}`}
      aria-placeholder="Callout title" className="font-medium outline-none" value={block.title}
      onChange={title => { updateBlock(block.id, { title }) }} />}
    <div data-alert-field="body" data-testid={`alert-body-${block.id}`} aria-placeholder="Callout content"
      className="text-on-surface-secondary flex min-w-0 flex-col gap-2.5 outline-none">{children}</div>
    {api.mode === 'edit' ? <span contentEditable={false} className="contents"><Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger render={<IconButton className={iconClass} data-testid={`alert-edit-${block.id}`} size="sm" type="button" variant="outlined" />}>
          {icon}Change callout tone
        </PopoverTrigger>
        <PopoverPopup align="end" className="w-72"><div className="grid gap-4">
          <div className="grid gap-1"><PopoverTitle>Callout tone</PopoverTitle><PopoverDescription>Choose the semantic tone. Edit title and content directly.</PopoverDescription></div>
          <div className="flex flex-wrap gap-2">{tones.map(color => <Button key={color} data-testid={`alert-color-${block.id}-${color}`}
            semanticRole={color} size="sm" toggled={color === block.color} variant={color === block.color ? 'filled' : 'outlined'}
            onClick={() => { updateBlock(block.id, { color }) }}>{color[0].toUpperCase() + color.slice(1)}</Button>)}</div>
          {block.title === undefined && <Button data-testid={`alert-add-title-${block.id}`} size="sm" variant="outlined"
            onClick={() => { updateBlock(block.id, { title: '' }); setOpen(false) }}>Add title</Button>}
          <Button data-testid={`alert-done-${block.id}`} size="sm" variant="filled" onClick={() => { setOpen(false) }}>{api.t('contentBlocksEditor.document.done')}</Button>
        </div></PopoverPopup>
      </Popover></span> : <span contentEditable={false} aria-hidden className={iconClass} data-slot="alert-icon">{icon}</span>}
    {/* The caret line exists only while editing; read and answer mode keep the card's bottom padding even with its top. */}
    {api.mode === 'edit' && <br />}
  </aside>
}

export const QuoteView: BlockView = ({ block, api, children }) => {
  if (block.type !== 'quote') return null
  return <blockquote data-block-id={block.id} className={blockEditorTheme.quote}>{children}
    {block.author && <RichField as="figcaption" value={block.author} api={api} data-quote-author="true" data-testid={`presentation-author-${block.id}`}
      aria-label="Quote attribution" aria-placeholder="Author or source"
      className='text-on-surface-secondary mt-2 min-h-5 text-right text-sm outline-none before:content-["—_"]'
      scope={{ blockId: block.id, field: 'author' }} />}
  </blockquote>
}

export const RuleView: BlockView = ({ block, children }) => block.type === 'rule'
  // Its parts sit 8px apart, as Notion spaces a statement and its list: the gap alone, no block margins on top.
  ? <section data-block-id={block.id} data-rule-key={block.key} data-rule-scope={block.scope}
    className="my-5 flex min-w-0 flex-col gap-2 outline-none [&>*]:my-0 [&>ol>li:first-child]:mt-0 [&>ul>li:first-child]:mt-0">{children}</section> : null

/** A question group: its context and questions, unframed (as a rule section). */
export const QuestionGroupView: BlockView = ({ block, children }) => block.type === 'questionGroup'
  ? <section data-block-id={block.id} data-question-group="true"
    className="my-5 flex min-w-0 flex-col gap-2 outline-none [&>*]:my-0">{children}</section> : null

export const ColumnsView: BlockView = ({ block, api, children }) => {
  if (block.type !== 'cols') return null
  const rendered = Children.toArray(children)
  return <div data-block-id={block.id} data-testid={`content-blocks-editor-columns-${block.id}`}
    className="group/columns relative my-5 flex min-w-0 flex-col items-start gap-5 sm:flex-row">
    {rendered.map((child, index) => <Fragment key={block.children[index]?.id ?? index}>
      {child}{api.mode === 'edit' && index < rendered.length - 1 && <ColumnResize api={api} left={block.children[index]} right={block.children[index + 1]}
        boundary={block.children.slice(0, index + 1).reduce((sum, col) => sum + (col.type === 'col' ? columnPercent(col.width) ?? 50 : 0), 0)} />}
    </Fragment>)}
  </div>
}

function ColumnResize({ left, right, api, boundary }: { left: Block; right: Block; api: CoreViewApi; boundary: number }) {
  const drag = useRef<{ x: number; width: number; leftWidth: number; rightWidth: number } | null>(null)
  if (left.type !== 'col' || right.type !== 'col') return null
  const pair = { leftWidth: columnPercent(left.width) ?? 50, rightWidth: columnPercent(right.width) ?? 50 }
  const resize = (value: typeof pair) => { api.dispatch({ type: 'resizeColumns', left: left.id, right: right.id, ...value }) }
  return <button type="button" contentEditable={false} aria-label="Resize columns" title="Resize columns"
    data-testid="content-blocks-editor-column-resize" data-column-resize="true"
    style={{ left: `${boundary}%` }}
    className="bg-border hover:bg-primary absolute inset-y-0 w-1 -translate-x-1/2 rounded-full touch-none cursor-col-resize opacity-0 group-hover/columns:opacity-100 focus:bg-primary focus:opacity-100"
    onKeyDown={event => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
      event.preventDefault(); event.stopPropagation()
      resize(keyResizeColumns(pair, event.key === 'ArrowLeft' ? -1 : 1))
    }} onPointerDown={event => {
      event.preventDefault(); event.stopPropagation()
      const siblings = event.currentTarget.parentElement?.querySelectorAll<HTMLElement>(':scope > [data-block-id]')
      const available = Array.from(siblings ?? []).filter(element => element.dataset.blockId === left.id || element.dataset.blockId === right.id)
        .reduce((sum, element) => sum + element.getBoundingClientRect().width, 0)
      if (!available) return
      drag.current = { x: event.clientX, width: available, ...pair }
      event.currentTarget.setPointerCapture(event.pointerId)
    }} onPointerMove={event => {
      if (!drag.current) return
      event.stopPropagation()
      resize(dragResizeColumns(drag.current, event.clientX - drag.current.x, drag.current.width))
    }} onPointerUp={event => {
      if (!drag.current) return
      event.stopPropagation(); drag.current = null
      event.currentTarget.releasePointerCapture(event.pointerId)
    }} onPointerCancel={() => { drag.current = null }} />
}

export const ColumnView: BlockView = ({ block, children }) => block.type === 'col'
  ? <section data-block-id={block.id} data-testid={`content-blocks-editor-column-${block.id}`}
    data-content-block-column-path={block.id}
    className="group/column relative w-full max-w-full min-w-0 sm:flex-[0_1_var(--content-block-column-width)]"
    style={{ '--content-block-column-width': `${columnPercent(block.width) ?? 50}%` } as CSSProperties}>{children}</section> : null

export const TableView: BlockView = ({ block, api, children }) => block.type === 'table'
  ? <div data-table-scrollable-wrapper="true" className={`my-5 relative w-full max-w-full min-w-0 overflow-x-auto overflow-y-hidden ${api.layout === 'touch' ? 'rounded-[28px]' : ''}`}>
    <table data-block-id={block.id} className={blockEditorTheme.table}>
      <tbody>{children}</tbody>
    </table>
  </div> : null

export const TableRowView: BlockView = ({ block, children }) => <tr data-block-id={block.id} className={blockEditorTheme.tableRow}>{children}</tr>

export const TableCellView: BlockView = ({ block, api, children }) => {
  if (block.type !== 'td') return null
  const header = '_isHeader' in block && block._isHeader === true
  const Tag = header ? 'th' : 'td'
  const selected = selectedCellIds(api.state).has(block.id)
  return <Tag data-block-id={block.id} rowSpan={block.rowSpan} colSpan={block.colSpan}
    className={`${header ? blockEditorTheme.tableCellHeader : blockEditorTheme.tableCell} ${selected ? blockEditorTheme.tableCellSelected : ''}`}>
    {block.children.some(child => ['p', 'list', 'hr', 'h', 'pre', 'table'].includes(child.type)) ? children : <p>{children}</p>}
  </Tag>
}
